import type React from 'react';
import { toast as stingToast } from '@chargebee/sting-react';

type ToastType = 'success' | 'error' | 'warning' | 'info' | 'default';

type TriggerOptions = {
  content: string | React.ReactElement;
  type?: ToastType;
  options?: {
    autoClose?: number | false;
    onClick?: () => void;
    onClose?: () => void;
    // Legacy options from react-toastify - accepted but may not all be supported
    toastId?: string;
    closeOnClick?: boolean;
    closeButton?: boolean;
    draggable?: boolean;
  };
};

// Map BlackToast types to Sting Toast variants
const typeToVariant = (
  type: ToastType
): 'success' | 'danger' | 'warning' | 'info' | 'primary' => {
  switch (type) {
    case 'success':
      return 'success';
    case 'error':
      return 'danger';
    case 'warning':
      return 'warning';
    case 'info':
      return 'info';
    default:
      return 'primary';
  }
};

// Store active toast dismiss functions for cleanup
const toastDismissFns = new Map<string, () => void>();

/**
 * Dismiss a toast by ID
 * @deprecated Use the dismiss function returned from toast() in '@chargebee/sting-react'
 */
export const dismissToast = (toastId: string): void => {
  const dismissFn = toastDismissFns.get(toastId);
  if (dismissFn) {
    dismissFn();
    toastDismissFns.delete(toastId);
  }
};

/**
 * Dismiss all active toasts. Useful for test cleanup.
 */
export const dismissAllToasts = (): void => {
  toastDismissFns.forEach((dismiss) => dismiss());
  toastDismissFns.clear();
};

/**
 * @deprecated Use `toast` from '@chargebee/sting-react' directly instead.
 *
 * Example migration:
 * ```tsx
 * // Before:
 * BlackToastTrigger({ content: 'Saved!', type: 'success' });
 *
 * // After:
 * import { toast } from '@chargebee/sting-react';
 * toast({ title: 'Saved!', variant: 'success' });
 * ```
 */
export const BlackToastTrigger = ({
  content,
  type = 'default',
  options,
}: TriggerOptions): string => {
  const variant = typeToVariant(type);

  // Extract text content
  const title = typeof content === 'string' ? content : 'Notification';

  // Handle duration - Sting uses milliseconds
  let duration = 3000; // default
  if (options?.autoClose === false) {
    duration = Infinity;
  } else if (typeof options?.autoClose === 'number') {
    duration = options.autoClose;
  }

  const { id, dismiss } = stingToast({
    variant,
    title,
    duration,
    dismissible: options?.closeButton !== false,
  });

  // Store dismiss function for later cleanup
  toastDismissFns.set(id, dismiss);

  // Also store with custom toastId if provided
  if (options?.toastId) {
    toastDismissFns.set(options.toastId, dismiss);
  }

  // Handle onClick by logging deprecation warning
  if (options?.onClick) {
    console.warn(
      '[BlackToastTrigger] onClick option is deprecated. Use primaryAction/secondaryAction props with Sting Toast instead.'
    );
  }

  // Call onClose callback when the toast is dismissed (via timeout)
  if (options?.onClose) {
    // Schedule the callback after the duration
    if (duration !== Infinity) {
      setTimeout(() => {
        options.onClose?.();
      }, duration);
    }
  }

  return options?.toastId ?? id;
};

/**
 * @deprecated Use `useToast` from '@chargebee/sting-react' directly.
 * Provides a dismiss function compatible with the old react-toastify API.
 */
export const toast = {
  dismiss: (toastId?: string) => {
    if (toastId) {
      dismissToast(toastId);
    }
  },
};
